/**
 * Slide 2 — Formação do Resultado
 * VERSÃO MINIMALISTA EXECUTIVA | Premium Board-Ready
 * 
 * Estrutura igual ao Slide 3:
 * 1. Título + Subtítulo descritivo
 * 2. Painel creme com dois Waterfalls lado a lado
 * 3. 3 mensagens executivas curtas
 * 
 * Canvas: 1600 x 900 px
 * 
 * ANIMAÇÕES:
 * - Fade-in do título e subtítulo
 * - Entrada escalonada das barras do waterfall
 * - Slide-up das mensagens executivas
 */

import React, { useMemo, useState, useEffect } from 'react';

// ============ DESIGN TOKENS ============
const COLORS = {
  pageBg: '#0A192F',           // Navy background
  contentBg: '#F7F6F2',        // Creme panel
  gold: '#C8A13A',
  textPrimary: '#0A192F',
  textSecondary: '#465467',
  textMuted: '#7A8594',
  textWhite: '#FFFFFF',
  // Waterfall colors
  positive: '#16A34A',         // Green for positive
  negative: '#C41E6D',         // Magenta for negative  
  endpoint: '#1E1B4B',         // Dark navy for start/end
};

const FONTS = {
  serif: '"Merriweather", Georgia, "Times New Roman", serif',
  sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

// ============ CSS KEYFRAMES (injected once) ============
const injectAnimationStyles = () => {
  const styleId = 'slide2-animations';
  if (document.getElementById(styleId)) return;
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes slideInFromLeft {
      from {
        opacity: 0;
        transform: translateX(-30px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes scaleIn {
      from {
        opacity: 0;
        transform: scaleX(0);
      }
      to {
        opacity: 1;
        transform: scaleX(1);
      }
    }
    
    @keyframes pulseGlow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(200, 161, 58, 0); }
      50% { box-shadow: 0 0 12px 2px rgba(200, 161, 58, 0.3); }
    }
  `;
  document.head.appendChild(style);
};

// ============ ANIMATED WATERFALL BAR ============
const AnimatedBar = ({ 
  label, 
  value, 
  barLeft, 
  barWidth, 
  barColor, 
  valueText, 
  valueColor, 
  barHeight, 
  yPos, 
  index,
  isAnimated,
  panelIndex = 0
}) => {
  const animationDelay = `${0.3 + panelIndex * 0.15 + index * 0.1}s`;
  
  return (
    <div 
      style={{ 
        position: 'absolute', 
        top: yPos, 
        left: 0, 
        right: 0, 
        height: barHeight, 
        display: 'flex', 
        alignItems: 'center',
        opacity: isAnimated ? 1 : 0,
        animation: isAnimated ? `slideInFromLeft 0.5s ease-out ${animationDelay} both` : 'none',
      }}
    >
      {/* Label */}
      <div style={{ 
        width: 140, 
        textAlign: 'right', 
        paddingRight: 16, 
        fontFamily: FONTS.sans, 
        fontSize: 12, 
        fontWeight: 600, 
        color: COLORS.textSecondary, 
        flexShrink: 0 
      }}>
        {label}
      </div>
      
      {/* Bar container */}
      <div style={{ flex: 1, position: 'relative', height: barHeight }}>
        {/* Bar */}
        <div
          style={{
            position: 'absolute',
            left: `${barLeft}%`,
            top: 0,
            width: `${Math.max(barWidth, 4)}%`,
            height: barHeight,
            backgroundColor: barColor,
            borderRadius: 6,
            transformOrigin: 'left center',
            animation: isAnimated ? `scaleIn 0.4s ease-out ${animationDelay} both` : 'none',
          }}
        />
        
        {/* Value */}
        <div
          style={{
            position: 'absolute',
            left: `${barLeft + barWidth + 2}%`,
            top: 0,
            height: barHeight,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 10,
            opacity: isAnimated ? 1 : 0,
            animation: isAnimated ? `fadeIn 0.3s ease-out ${parseFloat(animationDelay) + 0.2}s both` : 'none',
          }}
        >
          <span style={{ 
            fontFamily: FONTS.sans, 
            fontSize: 13, 
            fontWeight: 700, 
            color: valueColor, 
            whiteSpace: 'nowrap' 
          }}>
            {valueText}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============ HORIZONTAL WATERFALL CHART (Classic - same direction) ============
const WaterfallChart = ({ data, height = 360, isAnimated, panelIndex = 0, title }) => {
  // Process data for classic waterfall (all bars go same direction, stacked)
  const processedData = useMemo(() => {
    let runningTotal = 0;
    return data.map((item, index) => {
      const isFirst = index === 0;
      const isLast = index === data.length - 1;
      
      let barStart, barWidth;
      
      if (isFirst) {
        barStart = 0;
        barWidth = item.value;
        runningTotal = item.value;
      } else if (isLast) {
        barStart = 0;
        barWidth = item.value;
      } else {
        barStart = runningTotal;
        barWidth = Math.abs(item.value);
        runningTotal += item.value;
      }
      
      return { 
        ...item, 
        barStart,
        barWidth,
        runningEnd: runningTotal,
        isPositive: item.value >= 0, 
        isFirst, 
        isLast 
      };
    });
  }, [data]);

  // Find max value for scaling
  const maxValue = Math.max(
    ...data.filter((_, i) => i === 0 || i === data.length - 1).map(d => d.value)
  ) * 1.15;
  
  const scale = (value) => (value / maxValue) * 65;

  // Calculate bar dimensions
  const totalBars = data.length;
  const barHeight = Math.floor((height - 60) / totalBars) - 8;
  const gap = 8;

  return (
    <div style={{ width: '100%', height, position: 'relative' }}>
      {/* Chart title */}
      <div style={{ 
        fontFamily: FONTS.serif, 
        fontSize: 13, 
        fontWeight: 600, 
        color: COLORS.textPrimary,
        marginBottom: 16,
        paddingBottom: 8,
        borderBottom: `2px solid ${COLORS.gold}`
      }}>
        {title}
      </div>
      
      {/* Bars */}
      <div style={{ position: 'relative', height: height - 50 }}>
        {processedData.map((item, index) => {
          let barLeft, barWidth;
          
          if (item.isFirst || item.isLast) {
            barLeft = 0;
            barWidth = scale(item.barWidth);
          } else {
            barLeft = scale(item.barStart);
            barWidth = scale(item.barWidth);
          }
          
          // Colors
          let barColor;
          if (item.isFirst || item.isLast) {
            barColor = COLORS.endpoint;
          } else {
            barColor = item.isPositive ? COLORS.positive : COLORS.negative;
          }
          
          const yPos = index * (barHeight + gap);
          
          // Format value
          const valueText = `${item.value >= 0 ? '' : ''}${item.value.toFixed(1)}M`;
          
          const valueColor = (item.isFirst || item.isLast) 
            ? COLORS.endpoint 
            : (item.isPositive ? COLORS.positive : COLORS.negative);
          
          return (
            <AnimatedBar
              key={index}
              label={item.label}
              value={item.value}
              barLeft={barLeft}
              barWidth={Math.max(barWidth, 2)}
              barColor={barColor}
              valueText={valueText}
              valueColor={valueColor}
              barHeight={barHeight}
              yPos={yPos}
              index={index}
              isAnimated={isAnimated}
              panelIndex={panelIndex}
            />
          );
        })}
      </div>
    </div>
  );
};

// ============ EXECUTIVE MESSAGE CARD ============
const ExecutiveMessageCard = ({ number, title, text, index, isAnimated }) => {
  const animationDelay = `${1.2 + index * 0.15}s`;
  
  return (
    <div style={{
      flex: 1,
      backgroundColor: '#FFFFFF',
      borderRadius: 8,
      padding: '16px 20px',
      borderTop: `3px solid ${COLORS.gold}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      opacity: isAnimated ? 1 : 0,
      animation: isAnimated ? `fadeInUp 0.5s ease-out ${animationDelay} both` : 'none',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      cursor: 'default',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
    >
      {/* Number badge */}
      <div style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        backgroundColor: COLORS.pageBg,
        color: COLORS.textWhite,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: FONTS.sans,
        fontSize: 13,
        fontWeight: 700,
        transition: 'transform 0.3s ease',
      }}>
        {number}
      </div>
      
      {/* Title */}
      <p style={{
        fontFamily: FONTS.sans,
        fontSize: 13,
        fontWeight: 700,
        color: COLORS.textPrimary,
        margin: 0,
        lineHeight: 1.3,
      }}>
        {title}
      </p>
      
      {/* Text */}
      <p style={{
        fontFamily: FONTS.sans,
        fontSize: 12,
        fontWeight: 400,
        color: COLORS.textSecondary,
        margin: 0,
        lineHeight: 1.5,
      }}>
        {text}
      </p>
    </div>
  );
};

// ============ MAIN SLIDE ============
const IncomeStatementSlide2 = ({ data, language = 'pt' }) => {
  const [isAnimated, setIsAnimated] = useState(false);
  
  // Inject animation styles and trigger animations on mount
  useEffect(() => {
    injectAnimationStyles();
    const timer = setTimeout(() => setIsAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Default data
  const d = data || {
    ebitda1T25: 20.2, 
    ebitda1T26: 30.1,
    drivers1T25to1T26: { 
      volRegulares: 4.2, 
      volEspeciais: 3.1, 
      precoRegulares: -2.8, 
      precoEspeciais: -1.4, 
      efficiency: 3.8, 
      outros: 3.2 
    },
    ebitdaOrc1T26: 25.9,
    driversVsBudget: { 
      volRegulares: 1.4, 
      volEspeciais: 1.2, 
      precoRegulares: -0.8, 
      precoEspeciais: -0.6, 
      efficiency: 1.8, 
      outros: 1.2 
    },
  };

  // Waterfall 1: Year-over-Year Evolution
  const wf1 = [
    { label: 'EBITDA 1T25', value: d.ebitda1T25, isFirst: true },
    { label: '+ Vol. Regulares', value: d.drivers1T25to1T26.volRegulares },
    { label: '+ Vol. Especiais', value: d.drivers1T25to1T26.volEspeciais },
    { label: '- Preço Regulares', value: d.drivers1T25to1T26.precoRegulares },
    { label: '- Preço Especiais', value: d.drivers1T25to1T26.precoEspeciais },
    { label: '+ Eficiência', value: d.drivers1T25to1T26.efficiency },
    { label: '+ Outros', value: d.drivers1T25to1T26.outros },
    { label: 'EBITDA 1T26', value: d.ebitda1T26, isLast: true },
  ];

  // Waterfall 2: Budget Variance
  const wf2 = [
    { label: 'EBITDA Orç.', value: d.ebitdaOrc1T26, isFirst: true },
    { label: '± Vol. Regulares', value: d.driversVsBudget.volRegulares },
    { label: '± Vol. Especiais', value: d.driversVsBudget.volEspeciais },
    { label: '± Preço Regulares', value: d.driversVsBudget.precoRegulares },
    { label: '± Preço Especiais', value: d.driversVsBudget.precoEspeciais },
    { label: '± Eficiência', value: d.driversVsBudget.efficiency },
    { label: '± Outros', value: d.driversVsBudget.outros },
    { label: 'EBITDA Real', value: d.ebitda1T26, isLast: true },
  ];

  // Executive messages
  const messages = language === 'pt' ? [
    {
      title: 'Volume: motor do crescimento',
      text: 'Aumento de volume compensa pressão de preço e sustenta o crescimento do EBITDA.'
    },
    {
      title: 'Preço: principal pressão',
      text: 'Queda no preço médio representa o principal factor de pressão no trimestre.'
    },
    {
      title: 'Execução acima do plano',
      text: 'EBITDA supera orçamento em +16%, demonstrando disciplina operacional.'
    }
  ] : [
    {
      title: 'Volume: growth driver',
      text: 'Volume increase offsets price pressure and sustains EBITDA growth.'
    },
    {
      title: 'Price: main pressure',
      text: 'Average price decline represents the main pressure factor in the quarter.'
    },
    {
      title: 'Execution above plan',
      text: 'EBITDA exceeds budget by +16%, demonstrating operational discipline.'
    }
  ];

  // Editorial phrase
  const editorialPhrase = language === 'pt'
    ? 'O crescimento do EBITDA resulta do efeito volume, que mais do que compensa a pressão de preço.'
    : 'EBITDA growth results from volume effect, which more than offsets price pressure.';

  return (
    <div style={{ 
      width: 1600, 
      height: 900, 
      backgroundColor: COLORS.pageBg, 
      position: 'relative', 
      fontFamily: FONTS.sans, 
      overflow: 'hidden' 
    }}>
      
      {/* ============ BLOCO 1: TÍTULO + SUBTÍTULO ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        top: 48, 
        right: 60,
        opacity: isAnimated ? 1 : 0,
        animation: isAnimated ? 'fadeIn 0.6s ease-out 0s both' : 'none',
      }}>
        {/* Main title */}
        <h1 style={{ 
          fontFamily: FONTS.serif, 
          fontSize: 34, 
          lineHeight: '42px', 
          fontWeight: 700, 
          color: COLORS.textWhite, 
          margin: 0,
          animation: isAnimated ? 'fadeInUp 0.6s ease-out 0.1s both' : 'none',
        }}>
          {language === 'pt' ? 'Formação do Resultado' : 'Result Formation'}
        </h1>
        
        {/* Subtitle */}
        <p style={{ 
          fontFamily: FONTS.sans, 
          fontSize: 15, 
          color: '#64748B', 
          marginTop: 12,
          marginBottom: 0,
          opacity: isAnimated ? 1 : 0,
          animation: isAnimated ? 'fadeIn 0.5s ease-out 0.5s both' : 'none',
        }}>
          {language === 'pt'
            ? 'Decomposição do EBITDA 1T26: evolução homóloga e desvio face ao orçamento'
            : 'EBITDA Q1 26 breakdown: year-over-year evolution and budget variance'}
        </p>
      </div>

      {/* ============ BLOCO 2: PAINEL CREME COM 2 WATERFALLS ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        top: 150, 
        right: 60, 
        bottom: 200,
        backgroundColor: COLORS.contentBg, 
        borderRadius: 16, 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        opacity: isAnimated ? 1 : 0,
        animation: isAnimated ? 'fadeIn 0.5s ease-out 0.2s both' : 'none',
      }}>
        {/* Charts area - two side by side */}
        <div style={{ flex: 1, padding: '30px 40px 20px 40px', display: 'flex', gap: 40 }}>
          {/* Left Waterfall: Year-over-Year */}
          <div style={{ flex: 1 }}>
            <WaterfallChart 
              data={wf1} 
              height={400} 
              isAnimated={isAnimated} 
              panelIndex={0}
              title={language === 'pt' ? 'Evolução Homóloga (USD M)' : 'Year-over-Year Evolution (USD M)'}
            />
          </div>
          
          {/* Divider */}
          <div style={{ width: 1, backgroundColor: 'rgba(0,0,0,0.08)' }} />
          
          {/* Right Waterfall: Budget Variance */}
          <div style={{ flex: 1 }}>
            <WaterfallChart 
              data={wf2} 
              height={400} 
              isAnimated={isAnimated} 
              panelIndex={1}
              title={language === 'pt' ? 'Desvio vs Orçamento (USD M)' : 'Budget Variance (USD M)'}
            />
          </div>
        </div>
        
        {/* Editorial phrase */}
        <div style={{ 
          padding: '20px 40px 28px 40px',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          opacity: isAnimated ? 1 : 0,
          animation: isAnimated ? 'fadeIn 0.5s ease-out 1.1s both' : 'none',
        }}>
          <p style={{
            fontFamily: FONTS.serif,
            fontSize: 16,
            fontStyle: 'italic',
            color: COLORS.textSecondary,
            margin: 0,
            textAlign: 'center',
            lineHeight: 1.6
          }}>
            "{editorialPhrase}"
          </p>
        </div>
      </div>

      {/* ============ BLOCO 3: 3 MENSAGENS EXECUTIVAS ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        bottom: 56, 
        right: 60,
        display: 'flex',
        gap: 20
      }}>
        {messages.map((msg, i) => (
          <ExecutiveMessageCard
            key={i}
            number={i + 1}
            title={msg.title}
            text={msg.text}
            index={i}
            isAnimated={isAnimated}
          />
        ))}
      </div>

      {/* ============ FOOTER ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        right: 60, 
        bottom: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        opacity: isAnimated ? 1 : 0,
        animation: isAnimated ? 'fadeIn 0.4s ease-out 1.5s both' : 'none',
      }}>
        <span style={{ 
          fontFamily: FONTS.sans, 
          fontSize: 10, 
          color: '#64748B' 
        }}>
          {language === 'pt' 
            ? 'ABC | Demonstração de Resultados Executiva | Versão final de apresentação'
            : 'ABC | Executive Income Statement | Final presentation version'}
        </span>
        <span style={{ 
          fontFamily: FONTS.sans, 
          fontSize: 10, 
          color: COLORS.gold, 
          fontWeight: 600,
          animation: isAnimated ? 'pulseGlow 2s ease-in-out 2s infinite' : 'none',
          padding: '4px 12px',
          borderRadius: 4,
        }}>
          Board-ready
        </span>
        <span style={{ 
          fontFamily: FONTS.sans, 
          fontSize: 10, 
          color: '#64748B' 
        }}>
          2
        </span>
      </div>
    </div>
  );
};

export default IncomeStatementSlide2;
